/** Route-kind helpers for SEO paths. Pure. */
import type { SiteSeoMatch, SiteSeoRoute } from './seo-types';

const segments = (path: string): string[] => path.split('/');

/** The path has a dynamic segment (`/slots/:sourceId`). */
export const isPatternPath = (path: string): boolean => segments(path).some((s) => s.startsWith(':'));

/** The path is a footer wildcard (`/casino/*`, or the bare `*`). */
export const isWildcardPath = (path: string): boolean => path === '*' || path.endsWith('/*');

/** The match of an owner-added path; `set_pages` rows carry the server's own match instead. */
export const matchOfPath = (path: string): SiteSeoMatch => {
  if (isWildcardPath(path)) return 'wildcard';
  if (isPatternPath(path)) return 'pattern';
  return 'exact';
};

/**
 * The footer wildcard a pattern writes to: the pattern up to its first `:` segment
 * (`/slot-game/:gameId/:name` → `/slot-game/*`). Null when the first segment is dynamic (`/:lang/x` would derive
 * `/*`, which the hub never writes) or when the path has no dynamic segment.
 */
export const derivedWildcard = (pattern: string): string | null => {
  const parts = segments(pattern);
  const firstDynamic = parts.findIndex((s) => s.startsWith(':'));
  if (firstDynamic < 0) return null;
  const prefix = parts.slice(0, firstDynamic).join('/');
  return prefix === '' ? null : `${prefix}/*`;
};

/** `/x/*` → `/x/`. */
export const wildcardPrefix = (wildcard: string): string => (wildcard.endsWith('*') ? wildcard.slice(0, -1) : wildcard);

/** The footer rule path a route's `footer_text` is published under; null when it has none. */
export const footerRulePathOf = (route: Pick<SiteSeoRoute, 'path' | 'match'>): string | null =>
  route.match === 'pattern' ? derivedWildcard(route.path) : route.path;

/** A path the site would be on for this route, to resolve which footer rule it shows; null when there is none. */
export const footerProbePath = (route: Pick<SiteSeoRoute, 'path' | 'match'>): string | null => {
  if (route.match === 'exact') return route.path;
  if (route.match === 'wildcard') return wildcardPrefix(route.path);
  const derived = derivedWildcard(route.path);
  return derived === null ? null : wildcardPrefix(derived);
};
